/**
 * PATH: packages/drafting/src/panels/ToolPanel.tsx
 * FILE_ID: DRAFTING-PANEL-TOOL-001
 * PURPOSE: Top toolbar — tool picker + undo/redo + delete selection.
 */
import {
  MousePointer2,
  Minus,
  Square,
  Circle as CircleIcon,
  Type,
  Ruler,
  MessageSquare,
  Undo2,
  Redo2,
  Trash2,
} from 'lucide-react'
import type { Tool } from '../hooks/useDrafting'

const TOOLS: Array<{ id: Tool; label: string; Icon: typeof Minus }> = [
  { id: 'select', label: 'Select / move', Icon: MousePointer2 },
  { id: 'line', label: 'Line', Icon: Minus },
  { id: 'rect', label: 'Rectangle', Icon: Square },
  { id: 'circle', label: 'Circle', Icon: CircleIcon },
  { id: 'text', label: 'Text', Icon: Type },
  { id: 'dimension', label: 'Dimension', Icon: Ruler },
  { id: 'leader', label: 'Leader / callout', Icon: MessageSquare },
]

export interface ToolPanelProps {
  tool: Tool
  hasSelection: boolean
  canUndo: boolean
  canRedo: boolean
  onTool: (t: Tool) => void
  onUndo: () => void
  onRedo: () => void
  onDelete: () => void
}

export function ToolPanel(props: ToolPanelProps) {
  const { tool, hasSelection, canUndo, canRedo, onTool, onUndo, onRedo, onDelete } = props
  return (
    <div className="flex items-center gap-1 border-b border-slate-200 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900">
      {TOOLS.map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          title={label}
          aria-label={label}
          aria-pressed={tool === id}
          onClick={() => onTool(id)}
          className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
            tool === id
              ? 'bg-rose-600 text-white'
              : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
          }`}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </button>
      ))}
      <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
      <button
        type="button"
        title="Undo"
        aria-label="Undo"
        disabled={!canUndo}
        onClick={onUndo}
        className="flex h-8 w-8 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Undo2 className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        title="Redo"
        aria-label="Redo"
        disabled={!canRedo}
        onClick={onRedo}
        className="flex h-8 w-8 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Redo2 className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        title="Delete selection"
        aria-label="Delete selection"
        disabled={!hasSelection}
        onClick={onDelete}
        className="flex h-8 w-8 items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 disabled:opacity-40 dark:hover:bg-rose-950"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
    </div>
  )
}
